import { createContext, useCallback, useContext, useMemo } from "react";
import { clientFetch } from "../utils/clientFetch";

/**
 * 初回バックフィル操作 (#469 / 管理画面 Phase C) のフック。
 * apiKeyContext を雛形として踏襲。startBackfill (投入) / getJob (進捗確認) のみを担う。
 *
 * 注: rate_per_minute / 中止(abort) / 投入予定件数プレビューは現状バックエンド未対応のため扱わない。
 */

const API_BASE = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin`;

/** バックフィルジョブ (GET/POST 共通形)。件数系フィールドはサーバ実装に追従。 */
export type BackfillJob = {
  id: number;
  company_id: number;
  api_key_id: number;
  status: string;
  total_count?: number | null;
  enqueued_count?: number | null;
  error: string | null;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
};

export type StartBackfillInput = {
  api_key_id: number;
  from?: string | null;
  to?: string | null;
};

export type StartBackfillResult = { ok: true; job: BackfillJob } | { ok: false; message: string };
export type GetJobResult = { ok: true; job: BackfillJob } | { ok: false; message: string };

/** ジョブが終端状態 (これ以上ポーリング不要) か。 */
export function isTerminalStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return ["completed", "succeeded", "success", "done", "failed", "error", "canceled", "cancelled"].includes(
    status.toLowerCase()
  );
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.message || `エラーが発生しました (${res.status})`;
  } catch {
    return `エラーが発生しました (${res.status})`;
  }
}

export function useBackfillContext() {
  /** バックフィル投入 (201 で job を返す)。 */
  const startBackfill = useCallback(
    async (companyId: number, input: StartBackfillInput): Promise<StartBackfillResult> => {
      const body: Record<string, unknown> = { api_key_id: input.api_key_id };
      if (input.from) body.from = input.from;
      if (input.to) body.to = input.to;
      const res = await clientFetch(`${API_BASE}/companies/${companyId}/backfill`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) return { ok: false, message: await parseError(res) };
      const json = await res.json();
      return { ok: true, job: json.data as BackfillJob };
    },
    []
  );

  /** ジョブ進捗取得。 */
  const getJob = useCallback(async (companyId: number, jobId: number): Promise<GetJobResult> => {
    // 画面側が 3 秒ポーリングするため、ネットワーク障害時の共通トースト連発を抑止する。
    // エラー内容は戻り値 message で呼び出し側が扱える。
    const res = await clientFetch(`${API_BASE}/companies/${companyId}/backfill/${jobId}`, {
      method: "GET",
      suppressErrorNotification: true,
    });
    if (!res.ok) return { ok: false, message: await parseError(res) };
    const json = await res.json();
    return { ok: true, job: json.data as BackfillJob };
  }, []);

  return useMemo(() => ({ startBackfill, getJob }), [startBackfill, getJob]);
}

type BackfillContextValue = ReturnType<typeof useBackfillContext>;
const BackfillContext = createContext<BackfillContextValue | null>(null);

export function BackfillProvider({ children }: { children: React.ReactNode }) {
  const value = useBackfillContext();
  return <BackfillContext.Provider value={value}>{children}</BackfillContext.Provider>;
}

export function useBackfill(): BackfillContextValue {
  const ctx = useContext(BackfillContext);
  if (!ctx) throw new Error("useBackfill must be used within BackfillProvider");
  return ctx;
}
